import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { CircleCheck, Info } from 'lucide-react';
import { FloorplanAutoAssignPreview, FloorplanLevel } from '../../types/floorplan';
import { EmptyState, PersonCell, TableCard, TableSkeletonRows } from '../ui';
import { useDelayedFlag } from '../ui/useDelayedFlag';
import { sbg } from '../../theme/sbgTokens';
import { formatCount, formatNumber, MISSING } from '../../utils/format';
import { deskNumberOf } from './floorplan-panels/deskLabel';

interface SmartAssignDialogProps {
  open: boolean;
  teams: string[];
  levels: FloorplanLevel[];
  preview: FloorplanAutoAssignPreview | null;
  loading?: boolean;
  applying?: boolean;
  onClose: () => void;
  onPreview: (params: { mode: 'team' | 'all'; teamName?: string | null; levelId?: string | null }) => Promise<void>;
  onApply: (params: { mode: 'team' | 'all'; teamName?: string | null; levelId?: string | null }) => Promise<void>;
}

/** A one-line note under the preview summary: an icon and a sentence, no box. */
const PreviewNote: React.FC<{ tone: 'info' | 'success'; children: React.ReactNode }> = ({ tone, children }) => (
  <Box
    role="status"
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      px: 1.5,
      py: 1.25,
      borderRadius: sbg.radius.lg,
      border: `1px solid ${sbg.status[tone].border}`,
      bgcolor: sbg.status[tone].bg,
      color: sbg.status[tone].text,
    }}
  >
    <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', mt: '1px', color: sbg.status[tone].icon }}>
      {tone === 'success' ? <CircleCheck size={16} /> : <Info size={16} />}
    </Box>
    <Typography variant="body2" component="p" sx={{ color: 'inherit' }}>
      {children}
    </Typography>
  </Box>
);

const SmartAssignDialog: React.FC<SmartAssignDialogProps> = ({
  open,
  teams,
  levels,
  preview,
  loading = false,
  applying = false,
  onClose,
  onPreview,
  onApply,
}) => {
  const [mode, setMode] = useState<'team' | 'all'>('team');
  const [teamName, setTeamName] = useState('');
  const [levelId, setLevelId] = useState<string>('all');
  // The preview that was just applied, so the dialog can say so instead of
  // inviting a second apply of the same plan.
  const [appliedPreview, setAppliedPreview] = useState<FloorplanAutoAssignPreview | null>(null);
  const latestPreviewRef = useRef(preview);
  latestPreviewRef.current = preview;
  const showPreviewSkeleton = useDelayedFlag(loading);

  const levelLabelById = useMemo(
    () => new Map(levels.map((level) => [level.id, level.name])),
    [levels]
  );

  const handlePreview = async () => {
    await onPreview({
      mode,
      teamName: mode === 'team' ? teamName : null,
      levelId: levelId === 'all' ? null : levelId,
    });
  };

  const handleApply = async () => {
    const before = preview;
    await onApply({
      mode,
      teamName: mode === 'team' ? teamName : null,
      levelId: levelId === 'all' ? null : levelId,
    });
    const after = latestPreviewRef.current;
    if (after && after !== before) {
      setAppliedPreview(after);
    }
  };

  // The backend echoes the scope it previewed; a preview for another scope
  // must be refreshed before it can be applied.
  const previewMatchesSelection = Boolean(
    preview
    && preview.mode === mode
    && (preview.teamName || null) === (mode === 'team' ? teamName || null : null)
    && (preview.levelId || null) === (levelId === 'all' ? null : levelId)
  );
  const previewIsStale = Boolean(preview) && !previewMatchesSelection;
  const justApplied = Boolean(preview) && appliedPreview === preview;
  // Describe the scope the preview was built for, which may differ from the form.
  const previewScopeLabel = preview?.levelId ? levelLabelById.get(preview.levelId) || 'this floor' : 'any floor';
  const previewGroupLabel = preview?.mode === 'team' && preview.teamName ? preview.teamName : 'the roster';
  const unplaced = preview ? Math.max(0, preview.totalRequested - preview.totalPlaced) : 0;
  const applyDisabled = applying || !preview || preview.totalPlaced === 0 || previewIsStale || justApplied;
  const applyLabel = applying
    ? 'Assigning…'
    : preview && preview.totalPlaced > 0 && !previewIsStale
      ? `Assign ${formatCount(preview.totalPlaced, 'employee')}`
      : 'Assign desks';

  const renderPreviewBody = (current: FloorplanAutoAssignPreview) => {
    if (current.totalRequested === 0) {
      return (
        <EmptyState
          compact
          title="Everyone already has a desk"
          body={`No one on ${previewGroupLabel} is waiting for a desk.`}
        />
      );
    }

    if (current.totalPlaced === 0) {
      return (
        <EmptyState
          compact
          title="No open desks for this group"
          body={`${formatCount(current.totalRequested, 'employee')} ${current.totalRequested === 1 ? 'needs' : 'need'} a desk, but there are no open desks on ${previewScopeLabel}. Choose another floor, or clear desks first.`}
        />
      );
    }

    return (
      <>
        <Box>
          <Typography variant="h4" component="p" sx={{ color: sbg.text.primary }}>
            {`${formatNumber(current.totalPlaced)} of ${formatCount(current.totalRequested, 'employee')} can be placed`}
          </Typography>
          {unplaced > 0 && (
            <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
              {`${formatCount(unplaced, 'employee')} will stay unassigned. There aren’t enough open desks on ${previewScopeLabel}.`}
            </Typography>
          )}
        </Box>
        <TableCard maxHeight={280} aria-label="Proposed desk assignments">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Desk</TableCell>
                <TableCell>Floor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {current.assignments.map((item) => (
                <TableRow key={`${item.employee.id}-${item.desk.id}`}>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <PersonCell size={24} name={item.employee.name} />
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{deskNumberOf(item.desk) || MISSING}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{levelLabelById.get(item.desk.levelId) || MISSING}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableCard>
      </>
    );
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <Box role="status" aria-label="Previewing seating…" sx={{ visibility: showPreviewSkeleton ? 'visible' : 'hidden' }}>
          <TableCard>
            <Table size="small">
              <TableBody>
                <TableSkeletonRows columns={3} rows={4} delay={0} />
              </TableBody>
            </Table>
          </TableCard>
        </Box>
      );
    }

    if (!preview) {
      return (
        <Box sx={{ px: 2, py: 1.75, borderRadius: sbg.radius.lg, bgcolor: sbg.bg.surfaceSubtle }}>
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
            Preview to see who would sit where. Nothing changes until you assign desks.
          </Typography>
        </Box>
      );
    }

    return (
      <>
        {justApplied && (
          <PreviewNote tone="success">
            {`Assigned ${formatCount(preview.totalPlaced, 'employee')} to desks. Preview again to place anyone still waiting.`}
          </PreviewNote>
        )}
        {previewIsStale && !justApplied && (
          <PreviewNote tone="info">
            This preview is for an earlier selection. Preview again before assigning desks.
          </PreviewNote>
        )}
        {renderPreviewBody(preview)}
      </>
    );
  };

  return (
    <Dialog open={open} onClose={() => { if (!applying) onClose(); }} fullWidth maxWidth="sm" aria-labelledby="smart-assign-title">
      <DialogTitle id="smart-assign-title">Auto-assign desks</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2.5 }}>
          <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary }}>
            Seats people who don’t have a desk at open desks, near where their team already sits. No one who has a desk is moved.
          </Typography>

          <FormControl>
            <FormLabel id="smart-assign-mode-label" sx={{ mb: 0.5 }}>
              Who to place
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="smart-assign-mode-label"
              value={mode}
              onChange={(event) => setMode(event.target.value as 'team' | 'all')}
            >
              <FormControlLabel value="team" control={<Radio />} label="One team" />
              <FormControlLabel value="all" control={<Radio />} label="All teams" />
            </RadioGroup>
          </FormControl>

          <Box sx={{ display: 'grid', gap: 2, alignItems: 'start', gridTemplateColumns: { xs: '1fr', sm: mode === 'team' ? 'repeat(2, minmax(0, 1fr))' : '1fr' } }}>
            {mode === 'team' && (
              <TextField
                select
                fullWidth
                label="Team"
                value={teamName}
                onChange={(event) => setTeamName(event.target.value)}
                helperText={teams.length === 0 ? 'No teams on this floor plan yet.' : !teamName ? 'Choose a team to preview.' : undefined}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) =>
                    value ? (value as string) : (
                      <Box component="span" sx={{ color: sbg.text.tertiary }}>
                        Choose a team
                      </Box>
                    ),
                }}
              >
                {teams.map((team) => (
                  <MenuItem key={team} value={team}>
                    {team}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              select
              fullWidth
              label="Floors"
              value={levelId}
              onChange={(event) => setLevelId(event.target.value)}
            >
              <MenuItem value="all">All floors</MenuItem>
              {levels.map((level) => (
                <MenuItem key={level.id} value={level.id}>
                  {level.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box component="section" aria-labelledby="smart-assign-preview-title" sx={{ display: 'grid', gap: 1.25 }}>
            <Typography id="smart-assign-preview-title" variant="overline" component="h3" sx={{ color: sbg.text.secondary }}>
              Preview
            </Typography>
            {renderPreview()}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={applying}>
          Close
        </Button>
        <Button
          variant="outlined"
          onClick={handlePreview}
          disabled={loading || (mode === 'team' && !teamName)}
          aria-busy={loading || undefined}
        >
          {loading ? 'Previewing…' : preview ? 'Preview again' : 'Preview seating'}
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={applyDisabled}
          aria-busy={applying || undefined}
        >
          {applyLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SmartAssignDialog;
